/**
 * Sadhana API Service
 * Handles all weekly sadhana-related API calls
 * Phase 2 implementation with enhanced statistics
 */

import { ApiClient } from '../core/api-client';
import { ApiError, isApiError } from '../core/errors';
import { logger } from '../../utils/logger';
import {
  WeeklySadhanaCard,
  WeeklySadhanaCardSummary,
  WeeklySadhanaCardsResponse,
  WeeklySadhanaEntry,
  WeeklySadhanaEntryInput,
  SubmitWeeklySadhanaRequest,
  SubmitWeeklySadhanaResponse,
  MentorCommentRequest,
  MentorCommentResponse,
} from '../../types/sadhana';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class SadhanaService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get weekly sadhana card for current or specified week
   * @param memberId - Optional member ID (for mentors viewing devotees)
   * @param weekStart - Optional week start date (YYYY-MM-DD, must be Sunday)
   * @param useMentorEndpoint - Use the mentor-specific endpoint (for viewing devotee cards)
   * @returns Complete sadhana card with all entries and statistics
   */
  async getWeeklySadhanaCard(
    memberId?: number,
    weekStart?: string,
    useMentorEndpoint: boolean = false
  ): Promise<WeeklySadhanaCard | null> {
    try {
      const params = new URLSearchParams();
      
      // Use different parameter names based on endpoint
      if (useMentorEndpoint && memberId) {
        params.append('devoteeMemberId', memberId.toString());
      } else if (memberId) {
        params.append('memberId', memberId.toString());
      }
      
      if (weekStart) params.append('weekStartDate', weekStart);

      const queryString = params.toString();
      const endpoint = useMentorEndpoint
        ? `/congregation/mentor/sadhana/devotee/card${queryString ? `?${queryString}` : ''}`
        : `/congregation/sadhana/weekly/card${queryString ? `?${queryString}` : ''}`;

      console.log('API call - Member ID:', memberId, 'Week start:', weekStart, 'Endpoint:', endpoint);

      const response = await this.client.get<WeeklySadhanaCard>(endpoint, {
        credentials: 'include',
      });

      return response.data || null;
    } catch (error) {
      if (isApiError(error)) {
        if (error.statusCode === 404) {
          logger.warn('Sadhana card not found for the specified week');
          return null;
        }
        throw error;
      }
      throw error;
    }
  }

  /**
   * Get historical sadhana cards (paginated)
   * @param memberId - Optional member ID (for mentors)
   * @param limit - Number of cards to return (1-52, default 10)
   * @param offset - Pagination offset (default 0)
   * @returns Paginated list of card summaries
   */
  async getWeeklySadhanaCards(
    memberId?: number,
    limit: number = 10,
    offset: number = 0
  ): Promise<WeeklySadhanaCardsResponse> {
    try {
      const params = new URLSearchParams();
      if (memberId) params.append('memberId', memberId.toString());
      params.append('limit', Math.min(Math.max(limit, 1), 52).toString());
      params.append('offset', Math.max(offset, 0).toString());

      const response = await this.client.get<WeeklySadhanaCardsResponse>(
        `/congregation/sadhana/weekly/cards?${params.toString()}`,
        { credentials: 'include' }
      );

      return response.data || { cards: [], total: 0, limit, offset };
    } catch (error) {
      if (isApiError(error)) {
        logger.error('Failed to fetch sadhana cards', error);
        throw error;
      }
      throw error;
    }
  }

  /**
   * Create or update daily sadhana entry
   * @param entryData - Daily entry data
   * @returns Updated entry with calculated points
   */
  async upsertWeeklySadhanaEntry(
    entryData: WeeklySadhanaEntryInput
  ): Promise<WeeklySadhanaEntry> {
    try {
      const response = await this.client.post<WeeklySadhanaEntry>(
        '/congregation/sadhana/weekly/entry',
        entryData,
        { credentials: 'include' }
      );

      if (!response.data) {
        throw new ApiError('No data returned from server', 500);
      }

      logger.info('Sadhana entry saved successfully', { date: entryData.date });
      return response.data;
    } catch (error) {
      if (isApiError(error)) {
        if (error.statusCode === 400) {
          logger.error('Invalid sadhana entry data', error);
        }
        throw error;
      }
      throw error;
    }
  }

  /**
   * Submit weekly sadhana card to mentor
   * @param request - Submission request
   * @returns Success message and card summary
   */
  async submitWeeklySadhanaCard(
    request: SubmitWeeklySadhanaRequest
  ): Promise<SubmitWeeklySadhanaResponse> {
    try {
      const response = await this.client.post<SubmitWeeklySadhanaResponse>(
        '/congregation/sadhana/weekly/submit',
        request,
        { credentials: 'include' }
      );

      if (!response.data) {
        throw new ApiError('No data returned from server', 500);
      }

      logger.info('Sadhana card submitted successfully', {
        weekStartDate: request.weekStartDate,
      });
      return response.data;
    } catch (error) {
      if (isApiError(error)) {
        if (error.statusCode === 400) {
          logger.error('Cannot submit card - validation failed', error);
        }
        throw error;
      }
      throw error;
    }
  }

  /**
   * Add or update mentor comment on sadhana card (MENTOR/ADMIN only)
   * @param request - Mentor comment request
   * @returns Success message and updated card
   */
  async updateMentorComment(
    request: MentorCommentRequest
  ): Promise<MentorCommentResponse> {
    try {
      const response = await this.client.put<MentorCommentResponse>(
        '/congregation/sadhana/weekly/mentor-comment',
        request,
        { credentials: 'include' }
      );

      if (!response.data) {
        throw new ApiError('No data returned from server', 500);
      }

      logger.info('Mentor comment updated successfully', {
        cardId: request.cardId,
      });
      return response.data;
    } catch (error) {
      if (isApiError(error)) {
        if (error.statusCode === 403) {
          logger.error('Not authorized - requires MENTOR or ADMIN role', error);
          throw new ApiError('You must be a mentor or admin to add comments', 403);
        }
        throw error;
      }
      throw error;
    }
  }

  /**
   * Get mentor's assigned devotees with sadhana status
   * @returns List of devotees with name, email, status, and last submission date
   */
  async getMentorDevotees(): Promise<{ devotees: any[]; total: number }> {
    try {
      const response = await this.client.get<{ devotees: any[]; total: number }>(
        '/congregation/mentor/sadhana/devotees',
        { credentials: 'include' }
      );
      return {
        devotees: response.data?.devotees || [],
        total: response.data?.total || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch mentor devotees:', error);
      if (isApiError(error)) throw error;
      throw new ApiError('Failed to fetch devotees', 500);
    }
  }

  /**
   * Helper: Get current week's sadhana card
   */
  async getCurrentWeekCard(): Promise<WeeklySadhanaCard | null> {
    return this.getWeeklySadhanaCard();
  }

  /**
   * Helper: Check if user has submitted current week
   */
  async hasSubmittedCurrentWeek(): Promise<boolean> {
    const card = await this.getCurrentWeekCard();
    return card?.isSubmitted || false;
  }

  /**
   * Helper: Get weekly streak for current user
   */
  async getWeeklyStreak(): Promise<number> {
    const card = await this.getCurrentWeekCard();
    return card?.weeklyStreak || 0;
  }

  /**
   * Helper: Get best performing day for current user
   */
  async getBestDay(): Promise<{ day: number; name: string } | null> {
    const card = await this.getCurrentWeekCard();
    if (!card) return null;

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return {
      day: card.bestDay,
      name: days[card.bestDay] || 'Unknown',
    };
  }

  /**
   * Helper: Get daily average points for current week
   */
  async getDailyAveragePoints(): Promise<number> {
    const card = await this.getCurrentWeekCard();
    return card?.dailyAveragePoints || 0;
  }
}

// Export singleton instance
export const sadhanaService = new SadhanaService();
